import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getParameterFromURL } from '../../../utils/scripts';
import locales from '../../../content.json';

import avatarDefault from './img/mixed.png';
import s from './index.module.scss';

import americanPitBullTerrier from './img/americanPitBullTerrier.png';
import americanPitBullTerrierP from './img/americalPitBullTerrier-p.png';
import americanBully from './img/americanBully.png';
import americanBullyP from './img/americanBully-p.png';
import beagle from './img/beagle.png';
import beagleP from './img/beagle-p.png';
import borderCollie from './img/borderCollie.png';
import borderCollieP from './img/borderCollie-p.png';
import chihuahua from './img/chihuahua.png';
import chihuahuaP from './img/chihuahua-p.png';
import dachshund from './img/dachshund.png';
import dachshundP from './img/dachshund-p.png';
import frenchBulldog from './img/frenchBulldog.png';
import frenchBulldogP from './img/frenchBulldog-p.png';
import germanShepherd from './img/germanShepherd.png';
import germanShepherdP from './img/germanShepherd-p.png';
import goldenRetriever from './img/goldenRetriever.png';
import goldenRetrieverP from './img/goldenRetriever-p.png';
import goldendoodle from './img/goldendoodle.png';
import goldendoodleP from './img/goldendoodle-p.png';
import jackRussellTerrier from './img/jackRussellTerrier.png';
import jackRussellTerrierP from './img/jackRussellTerrier-p.png';
import labradorRetriever from './img/labradorRetriever.png';
import labradorRetrieverP from './img/labradorRetriever-p.png';
import maltipoo from './img/maltipoo.png';
import maltipooP from './img/maltipoo-p.png';
import mixed from './img/mixed.png';
import mixedP from './img/mixed-p.png';
import pomeranian from './img/pomeranian.png';
import pomeranianP from './img/pomeranian-p.png';
import poodle from './img/poodle.png';
import poodleP from './img/poodle-p.png';
import shihTzu from './img/shihTzu.png';
import shihTzuP from './img/shihTzu-p.png';
import siberianHusky from './img/siberianHusky.png';
import siberianHuskyP from './img/siberianHusky-p.png';
import yorkshireTerrier from './img/yorkshireTerrier.png';
import yorkshireTerrierP from './img/yorkshireTerrier-p.png';

const BREED_IMAGES = {
  'american pit bull terrier': [americanPitBullTerrier, americanPitBullTerrierP],
  'american bully': [americanBully, americanBullyP],
  'beagle': [beagle, beagleP],
  'border collie': [borderCollie, borderCollieP],
  'chihuahua': [chihuahua, chihuahuaP],
  'dachshund': [dachshund, dachshundP],
  'french bulldog': [frenchBulldog, frenchBulldogP],
  'german shepherd': [germanShepherd, germanShepherdP],
  'golden retriever': [goldenRetriever, goldenRetrieverP],
  'goldendoodle': [goldendoodle, goldendoodleP],
  'jack russell terrier': [jackRussellTerrier, jackRussellTerrierP],
  'labrador retriever': [labradorRetriever, labradorRetrieverP],
  'maltipoo': [maltipoo, maltipooP],
  'pomeranian': [pomeranian, pomeranianP],
  'poodle': [poodle, poodleP],
  'shih tzu': [shihTzu, shihTzuP],
  'siberian husky': [siberianHusky, siberianHuskyP],
  'yorkshire terrier': [yorkshireTerrier, yorkshireTerrierP],
};

const getBreedImage = (breedName, role) => {
  const isPuppy = role === 'puppy';
  const key = breedName ? breedName.toLowerCase().trim() : '';
  const pair = BREED_IMAGES[key];
  if (pair) {
    return isPuppy ? pair[1] : pair[0];
  }
  return isPuppy ? mixedP : mixed;
};

const BREED_LABELS = {
  v1: 'Mixed Breed',
  v2: 'Labrador Retriever',
  v3: 'Golden Retriever',
  v4: 'Siberian Husky',
  v5: 'American Pit Bull Terrier',
  v6: 'French Bulldog',
  v7: 'German Shepherd',
  v8: 'Other',
};

const AGE_LABELS = {
  '0-6m': '0-6 months',
  '7-12m': '7-12 months',
  '1-2y': '1-2 years',
  '2-7y': '2-7 years',
  more7y: 'More than 7 years',
  '0-6 months': '0-6 months',
  '7-12 months': '7-12 months',
  '1-2 years': '1-2 years',
  '2-7 years': '2-7 years',
  'More than 7 years': 'More than 7 years',
};

const getDogName = rawName => {
  if (!Array.isArray(rawName)) {
    return '';
  }

  const value = rawName.find(item => typeof item === 'string' && item.trim().length > 0);
  return value || '';
};

const getBreed = rawBreed => {
  if (!rawBreed) {
    return 'Mixed Breed';
  }

  const breedKey = String(rawBreed).split('_')[0];
  return BREED_LABELS[breedKey] || 'Mixed Breed';
};

const getAge = rawAge => {
  if (!rawAge) {
    return '7-12 months';
  }

  if (Array.isArray(rawAge)) {
    const match = rawAge.find(item => typeof item === 'string' && AGE_LABELS[item]);
    return AGE_LABELS[match] || rawAge.find(item => typeof item === 'string') || '7-12 months';
  }

  return AGE_LABELS[rawAge] || '7-12 months';
};

const getIssues = rawIssues => {
  if (!Array.isArray(rawIssues) || rawIssues.length === 0) {
    return 'Barking, Biting, Barking, Barking';
  }

  return rawIssues.join(', ');
};

const escapeHtml = value => {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll('\'', '&#39;');
};

const replaceSpanContent = (html, dogName) => {
  if (!dogName || typeof html !== 'string') {
    return html;
  }

  return html.replace(/(<span\b[^>]*>)(.*?)(<\/span>)/i, `$1${escapeHtml(dogName)}$3`);
};

const getProfileParamsFromUrl = () => {
  if (typeof window === 'undefined') {
    return {
      dogName: '',
      breedValue: '',
      puppyValue: '',
      issuesDisplay: '',
    };
  }

  const params = new URLSearchParams(window.location.search);

  return {
    dogName: params.get('dogName') || '',
    breedValue: params.get('breedValue') || '',
    puppyValue: params.get('puppyValue') || '',
    issuesDisplay: params.get('issuesDisplay') || '',
    breedName: params.get('breedName') || '',
    role: params.get('role') || '',
  };
};

const TrainingProfile = () => {
  const name = useSelector(state => state.data.name);
  const age = useSelector(state => state.data.age);
  const issues = useSelector(state => state.data.issues);
  const lang =
    (typeof window !== 'undefined' && window.userLang && locales[window.userLang])
      ? window.userLang
      : 'en';
  const dict = locales[lang];

  const profile = useMemo(() => {
    const {
      dogName: dogNameFromUrl,
      breedValue,
      puppyValue,
      issuesDisplay,
      breedName,
      role,
    } = getProfileParamsFromUrl();
    const dogName = dogNameFromUrl || getDogName(name);

    return {
      dogName: dogName || 'Your dog',
      customDogName: dogName,
      breed: breedValue || getBreed(getParameterFromURL('customV1_breed')),
      age: puppyValue || getAge(age),
      issues: issuesDisplay || getIssues(issues),
      level: dict.payment_text_352,
      avatarImg: getBreedImage(breedName, role),
    };
  }, [age, issues, name]);

  const titleHtml = useMemo(() => {
    return replaceSpanContent(dict.payment_text_320, profile.customDogName);
  }, [dict.payment_text_320, profile.customDogName]);

  return (
    <div className={s.wrapper}>
      <div className={s.content}>
        <h2 className={s.title} dangerouslySetInnerHTML={{ __html: titleHtml }} />

        <div className={s.card}>
          <div className={s.header}>
            <span>{locales[window.userLang].payment_text_321}</span>
          </div>

          <div className={s.avatarWrapper}>
            <div className={s.avatarImageWrapper}>
              <img className={s.avatar} src={profile.avatarImg || avatarDefault} alt={`${profile.dogName} avatar`} />
            </div>
          </div>

          <div className={s.cardBody}>
            <div className={s.row}>
              <span className={s.label}>{locales[window.userLang].payment_text_322}:</span>
              <span className={s.value}>{profile.breed}</span>
            </div>

            <div className={s.row}>
              <span className={s.label}>{locales[window.userLang].payment_text_323}:</span>
              <span className={s.value}>{profile.age}</span>
            </div>

            <div className={`${s.row} ${s.rowTop}`} style={{ display: 'block' }}>
              <span className={s.label}>{locales[window.userLang].payment_text_324}:&nbsp;</span>
              <span className={s.value}>{profile.issues}</span>
            </div>

            <div className={s.row}>
              <span className={s.label}>{locales[window.userLang].payment_text_325}:</span>
              <span className={s.value}>{profile.level}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { TrainingProfile };
export default TrainingProfile;
